import { lazy } from 'react';

/**
 * Wraps React.lazy() to handle chunk load failures after deployments.
 *
 * When Vite rebuilds, chunk hashes change. Users with a cached index.html
 * still reference old chunk URLs that return 404. This wrapper catches the
 * import error and reloads the page once to fetch fresh HTML.
 *
 * A sessionStorage flag prevents infinite reload loops.
 *
 * Fixes: JETOGO-FRONTEND-F ("TypeError: Importing a module script failed")
 */
export function lazyWithRetry(importFn) {
  return lazy(async () => {
    const storageKey = 'lazyWithRetry_reloaded';

    try {
      const module = await importFn();
      // Success — clear any previous reload flag
      sessionStorage.removeItem(storageKey);
      return module;
    } catch (error) {
      const isChunkError =
        error.message?.includes('Importing a module script failed') ||
        error.message?.includes('Failed to fetch dynamically imported module') ||
        error.message?.includes('Unable to preload CSS') ||
        error.message?.includes('error loading dynamically imported module') ||
        error.name === 'ChunkLoadError';

      const hasReloaded = sessionStorage.getItem(storageKey);

      if (isChunkError && !hasReloaded) {
        sessionStorage.setItem(storageKey, '1');
        window.location.reload();
        // Return a never-resolving promise so React doesn't render stale content
        return new Promise(() => {});
      }

      // Either not a chunk error, or we already reloaded once — throw
      throw error;
    }
  });
}
